<template>
  <div>
    <CustomTable :first-cell-image="true" :custom-class="'view-players-table hr-players-table double-image'">
      <template slot="theader">
        <TableRow>
          <TableCell :class="{'fixed-column first-col' :scrollLeftOver25}">
            <div class="gear-btn-wrapper gear-stats">
              <b-btn :id="'metricInfoPopover_HrStatsTable'" :class="'popover-btn'">
                <img src="@/assets/images/icons/gear-icon.png" alt="unit settings icon">
              </b-btn>

              <div class="custom-popover table-settings-popover" id="popoverPlacement_HrStatsTable"></div>

              <b-popover 
                :target="`metricInfoPopover_HrStatsTable`" 
                triggers="click blur" 
                placement="bottomright" 
                container="popoverPlacement_HrStatsTable">

                <div class="popover-content">
                  <div class="table-setting-row">
                    <div class="row-content">
                      <div class="row-content-item">
                        <span class="row-label">{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.avgHr')}}</span>
                        <div 
                          class="btn-group calendar-view-filter switch-btn-group switch-btn-group-orange fa-pull-right"
                          @click="changeAvgHrUnitOnClick()">
                          <label class="btn" 
                            :class="{'active': avgHrSelectedUnit === hrUnits.PERCENTAGE}">%</label>
                          <label class="btn" 
                            :class="{'active': avgHrSelectedUnit === hrUnits.BPM}">{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.bpm')}}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="table-setting-row">
                    <div class="row-content">
                      <div class="row-content-item">
                        <span class="row-label">{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.topHr')}}</span>
                        <div 
                          class="btn-group calendar-view-filter switch-btn-group switch-btn-group-orange fa-pull-right"
                          @click="changeTopHrUnitOnClick()">
                          <label class="btn" 
                            :class="{'active': topHrSelectedUnit === hrUnits.PERCENTAGE}">%</label>
                          <label class="btn" 
                            :class="{'active': topHrSelectedUnit === hrUnits.BPM}">{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.bpm')}}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-popover>
            </div>
            <span :class="{'d-none':scrollLeftOver25}">{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.playerSegment')}}</span>
          </TableCell>
          <TableCell>>80%</TableCell>
          <TableCell>{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.avgHr')}}</TableCell>
          <TableCell>{{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.topHr')}}</TableCell>
          <TableCell>0-50%</TableCell>
          <TableCell :custom-classes="'purple-column'">50-60%</TableCell>
          <TableCell :custom-classes="'green-column'">60-70%</TableCell>
          <TableCell :custom-classes="'yellow-column'">70-80%</TableCell>
          <TableCell :custom-classes="'orange-column'">80-90%</TableCell>
          <TableCell :custom-classes="'red-column'">90%+</TableCell>
        </TableRow>
      </template>
    </CustomTable>

    <CustomTable 
      :first-cell-image="true" 
      :custom-class="'accordion-table view-players-table hr-players-table double-image'">
      <template slot="tbody">
        <TableRow>
          <TableCell :class="{'fixed-column data-col':scrollLeftOver25}">
            <PlayerInvalidActivityIcon :activity-status="activityInfo.status" :activity-type="activityInfo.type" :hrData="data" v-if="!scrollLeftOver25"/>

            <span class="battery-warnings" v-if="!scrollLeftOver25 && activityInfo.batteryLevelIsLow" v-b-tooltip="{
                title: this.$t('CALENDAR_ACTIVITY.playerStatsTables.batteryWarningsTooltip') ,
                container: 'body',
                boundary: 'viewport',
                trigger: 'hover',
                template: tooltipTemplate,
                placement: 'topright'
              }">
              <img src="@/assets/images/activity/10_battery.png" alt="battery warning">
            </span>

            <span 
              v-if="validHrWarning"
              v-b-tooltip="{
                title: this.$t('CALENDAR_ACTIVITY.playerStatsTables.beltNotWornCorrectly'),
                container: 'body',
                boundary: 'viewport',
                trigger: 'hover',
                template: tooltipTemplate,
                placement: 'topright'
              }"
              class="warning-icon other-img error-icon cursor-pointer">
              <i class="fas fa-heartbeat" aria-hidden="true"></i>
            </span>

            <img class="player-icon cell-img" v-if="activityInfo.playerPhoto" :src="activityInfo.playerPhoto" alt="player photo">

            <CustomEmblem 
              v-if="!activityInfo.playerPhoto"
              :gender="activityInfo.playerGender"
              :sub-header="[activityInfo.playerFirstName, activityInfo.playerLastName]"
              :custom-classes="'cell-img'"
              id="tooltip-full-session" />
              <span :class="{'d-none':scrollLeftOver25}">
              {{ $t('CALENDAR_ACTIVITY.playerStatsTables.hrStatsTable.fullSession')}} ({{activityInfo.durationInSeconds | secondsToMinutesSeconds}})
              </span>
              <b-tooltip v-if="scrollLeftOver25" target="tooltip-full-session" triggers="hover">
                {{activityInfo.playerFirstName}} {{activityInfo.playerLastName}}
              </b-tooltip>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="data.timeAbove80PercentHrSeconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>
              <template v-if="data.timeAbove80PercentHrSeconds !== null">
                {{ data.timeAbove80PercentHrSeconds | secondsToMinutesSeconds}}
              </template>
            </template>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>

            <template v-if="avgHrSelectedUnit === hrUnits.BPM && !data.invalid">
              {{ data.avgHeartRate != null ? data.avgHeartRate : $t('COMMON.notApplicable') }}
            </template>

            <template v-if="avgHrSelectedUnit === hrUnits.PERCENTAGE && !data.invalid">
              {{ data.avgHeartRatePercentage != null ? `${data.avgHeartRatePercentage}%` : $t('COMMON.notApplicable') }}
            </template>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>

            <template v-if="topHrSelectedUnit === hrUnits.BPM && !data.invalid">
              {{ data.topHeartRate != null ? data.topHeartRate : $t('COMMON.notApplicable') }}
            </template>

            <template v-if="topHrSelectedUnit === hrUnits.PERCENTAGE && !data.invalid">
              {{ data.maxHeartRatePercentage != null ? `${data.maxHeartRatePercentage}%` : $t('COMMON.notApplicable') }}
            </template>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="data.hrZone1Seconds === null">
               {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="data.hrZone1Seconds !== null">
                {{ data.hrZone1Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'purple-column'">
            <template v-if="data.invalid">
             {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="data.hrZone2Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="data.hrZone2Seconds !== null">
                {{ data.hrZone2Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'green-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>

            <template v-if="!data.invalid">
              <template v-if="data.hrZone3Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="data.hrZone3Seconds !== null">
                {{ data.hrZone3Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'yellow-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="data.hrZone4Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="data.hrZone4Seconds !== null">
                {{ data.hrZone4Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'orange-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="data.hrZone5Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="data.hrZone5Seconds !== null">
                {{ data.hrZone5Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
            
          </TableCell>
          <TableCell :custom-classes="'red-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="data.hrZone6Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="data.hrZone6Seconds !== null">
                {{ data.hrZone6Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
        </TableRow>
      </template>
    </CustomTable>

    <CustomTable 
      v-for="segment in statsSegment"
      :key="segment.id"
      :first-cell-image="true" 
      :custom-class="'accordion-table view-players-table hr-players-table double-image'">
      <template slot="tbody">
        <TableRow>
          <TableCell :class="{'fixed-column data-col':scrollLeftOver25}">
            <PlayerInvalidActivityIcon :activity-status="activityInfo.status" :activity-type="activityInfo.type" v-if="!scrollLeftOver25" />

            <img class="player-icon cell-img" v-if="activityInfo.playerPhoto" :src="activityInfo.playerPhoto" alt="player photo">

            <CustomEmblem 
              v-if="!activityInfo.playerPhoto"
              :gender="activityInfo.playerGender"
              :sub-header="[activityInfo.playerFirstName, activityInfo.playerLastName]"
              :custom-classes="'cell-img'" 
              id="tooltip-segment"/>
              <span :class="{'d-none':scrollLeftOver25}">
                {{segment.name}} ({{(segment.endPoint - segment.startPoint) | secondsToMinutesSeconds}})
              </span>
              <b-tooltip v-if="scrollLeftOver25" target="tooltip-segment" triggers="hover">
                {{activityInfo.playerFirstName}} {{activityInfo.playerLastName}}
              </b-tooltip>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.timeAbove80PercentHrSeconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>
              <template v-if="segment.segmentStats.timeAbove80PercentHrSeconds !== null">
                {{ segment.segmentStats.timeAbove80PercentHrSeconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>

            <template v-if="avgHrSelectedUnit === hrUnits.BPM && !data.invalid">
              {{ segment.segmentStats.avgHeartRate != null ? segment.segmentStats.avgHeartRate : $t('COMMON.notApplicable') }}
            </template>

            <template v-if="avgHrSelectedUnit === hrUnits.PERCENTAGE && !data.invalid">
              {{ segment.segmentStats.avgHeartRatePercentage != null ? `${segment.segmentStats.avgHeartRatePercentage}%` : $t('COMMON.notApplicable') }}
            </template>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>

            <template v-if="topHrSelectedUnit === hrUnits.BPM && !data.invalid">
              {{ segment.segmentStats.topHeartRate != null ? segment.segmentStats.topHeartRate : $t('COMMON.notApplicable') }}
            </template>

            <template v-if="topHrSelectedUnit === hrUnits.PERCENTAGE && !data.invalid">
              {{ segment.segmentStats.maxHeartRatePercentage != null ? `${segment.segmentStats.maxHeartRatePercentage}%` : $t('COMMON.notApplicable') }}
            </template>
          </TableCell>
          <TableCell>
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.hrZone1Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="segment.segmentStats.hrZone1Seconds !== null">
                {{ segment.segmentStats.hrZone1Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'purple-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.hrZone2Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="segment.segmentStats.hrZone2Seconds !== null">
                {{ segment.segmentStats.hrZone2Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'green-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>

            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.hrZone3Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="segment.segmentStats.hrZone3Seconds !== null">
                {{ segment.segmentStats.hrZone3Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'yellow-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.hrZone4Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="segment.segmentStats.hrZone4Seconds !== null">
                {{ segment.segmentStats.hrZone4Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
          <TableCell :custom-classes="'orange-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.hrZone5Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="segment.segmentStats.hrZone5Seconds !== null">
                {{ segment.segmentStats.hrZone5Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
            
          </TableCell>
          <TableCell :custom-classes="'red-column'">
            <template v-if="data.invalid">
              {{ $t('COMMON.notApplicable')}}
            </template>
            <template v-if="!data.invalid">
              <template v-if="segment.segmentStats.hrZone6Seconds === null">
                {{ $t('COMMON.notApplicable')}}
              </template>

              <template v-if="segment.segmentStats.hrZone6Seconds !== null">
                {{ segment.segmentStats.hrZone6Seconds | secondsToMinutesSeconds }}
              </template>
            </template>
          </TableCell>
        </TableRow>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import { CustomTable, TableRow, TableCell } from '@/components/common/table'
import CustomEmblem from '@/components/common/CustomEmblem'
import { TIME_ABOVE_80_PERCENT_HR_UNITS } from '@/constants/SessionConsts'
import PlayerInvalidActivityIcon from '@/components/calendar-activity/PlayerInvalidActivityIcon'
import { TOASTER_WARNING } from '@/common/events.types';

export default {
  name: 'HrStatsTable',
  props: [
    'data',
    'activityInfo',
    'avgHrSelectedUnit',
    'topHrSelectedUnit',
    'changeAvgHrUnit',
    'changeTopHrUnit',
    'statsSegment',
    'scrollLeftOver25'
  ],
  data () {
    return {
      hrUnits: TIME_ABOVE_80_PERCENT_HR_UNITS,
      tooltipTemplate: '<div class="tooltip event-tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>',
      validHrWarning: this.data.validHrDataPercentage > 50 && this.data.validHrDataPercentage < 85
    }
  },
  mounted() {
    if (this.validHrWarning) {
      this.$eventHub.$emit(TOASTER_WARNING, { message: this.$t('CALENDAR_ACTIVITY.playerStatsTables.beltNotWornCorrectly') })
    }
  },

  methods: {
    changeAvgHrUnitOnClick () {
      const newUnit = this.avgHrSelectedUnit === this.hrUnits.PERCENTAGE ? this.hrUnits.BPM : this.hrUnits.PERCENTAGE
      this.changeAvgHrUnit(newUnit)
    },
    changeTopHrUnitOnClick () {
      const newUnit = this.topHrSelectedUnit === this.hrUnits.PERCENTAGE ? this.hrUnits.BPM : this.hrUnits.PERCENTAGE
      this.changeTopHrUnit(newUnit)
    }
  },
  components: {
    CustomTable,
    TableRow,
    TableCell,
    CustomEmblem,
    PlayerInvalidActivityIcon
  }
}
</script>

<style lang="scss" scoped>
  .hr-players-table {
    table-layout: fixed;

    .table-cell {
      &:first-child {
        width: 30%;
      }

      &:nth-child(2), &:nth-child(3) {
        width: 10%;
      }

      &:nth-child(n+3) {
        width: 9%;
      }

      &.purple-column {
        color: $melrose;
      }
    }

    .warning-icon {
      color: $tangerine;
      opacity: .8;
    }
  }
</style>

